import { useMemo } from 'react';
import { useQuery } from 'react-query';
import usePlanStore, { DdxSetup } from 'store/planStore';
import { Countries } from 'shared/enums';
import {
  DDX_SETUP_KEY,
  fetchDDXSetup,
  ApiResponse,
} from '../../../../../../../api/services/mediaPlanning/getDDXSetup';

type DdxSetupByCountry = Record<string, Partial<DdxSetup> | undefined>;

function countryNameToISO(countryName: string): string | null {
  return (
    (Countries as unknown as Record<string, string>)[
      countryName.toUpperCase()
    ] ?? null
  );
}

export default function useDdxSetup() {
  const { division, brand } = usePlanStore((s) => s.generalInfoState);
  const storedCountryForms = usePlanStore((s) => s.storedCountryForms);
  const setDdxSetupByCountry = usePlanStore((s) => s.setDdxSetupByCountry);

  const body = useMemo(() => {
    return Object.entries(storedCountryForms)
      .map(([countryKey, form]) => {
        const iso = countryNameToISO(countryKey) ?? countryKey;

        return {
          division,
          brand,
          countryISO: iso,
          campaignScenario: form.campaignScenario ?? '',
        };
      })
      .filter(
        (x) =>
          !!x.division && !!x.brand && !!x.countryISO && !!x.campaignScenario
      );
  }, [storedCountryForms, division, brand]);

  const queryKey = useMemo(
    () => [
      DDX_SETUP_KEY,
      division,
      brand,
      body.map((b) => `${b.countryISO}:${b.campaignScenario}`).join('|'),
    ],
    [division, brand, body]
  );

  return useQuery<ApiResponse<DdxSetupByCountry>, Error>(
    queryKey,
    () => fetchDDXSetup(body),
    {
      enabled: !!division && !!brand && body.length > 0,
      onSuccess: (res) => {
        const data = res.data ?? {};

        setDdxSetupByCountry(data);
      },

      onError: () => setDdxSetupByCountry({}),
      refetchOnWindowFocus: false,
    }
  );
}
